import {ChangeEvent, FC, useCallback, useRef, useState} from "react";
import apiClient from "../lib/api";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {styled} from "@mui/material/styles";
import Filter from "@mui/icons-material/Filter";
import {useFeedback} from "@memaro/feedback";

type ProfileContainerProps = {
    size?: number
}

const ProfileContainer = styled(Box)<ProfileContainerProps>(({size = 150, theme}) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `inset 4px 4px 4px ${theme.palette.secondary.dark}, inset -4px -4px 4px ${theme.palette.secondary.light}`,
    position: 'relative',
    img: {
        width: '96%',
        height: '96%',
        objectFit: 'cover',
        borderRadius: '50%',
    }
}));

type OverlayProps = {
    visible: 'hover' | 'always'
}

const Overlay = styled(Box)<OverlayProps>(({visible, theme}) => ({
    background: theme.palette.background.paper,
    transition: 'opacity 0.3s',
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    opacity: visible === 'always' ? 0.7 : 0,
    ':hover': {
        opacity: 0.7,
    },
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.main,
        fontSize: 50,
    }
}))

export type ProfilePictureProps = ProfileContainerProps & {
    profileID: string
    src: string
    onChange?: (url: string) => void
    editMode?: boolean
}

const ProfilePicture: FC<ProfilePictureProps> = ({profileID, src, onChange, editMode, size}) => {
    const ref = useRef<HTMLInputElement | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const {error} = useFeedback()

    const handleClick = useCallback(() => {
        ref.current?.click();
    }, [])

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setLoading(true)
            apiClient.upload(profileID, "profile_picture", event.target.files?.[0])
                .then(onChange)
                .catch(() => error?.("Er is een fout opgetreden bij het uploaden of het verwerken van de foto"))
                .finally(() => setLoading(false))
            if (ref.current) {
                ref.current!.value = ""
            }
        }
    }, [profileID, onChange, error])

    return (
        <ProfileContainer onClick={loading || !editMode ? undefined : handleClick} size={size}>
            <img src={src} alt="Profiel foto"/>
            {loading ? (
                <Overlay visible="always">
                    <CircularProgress/>
                </Overlay>
            ) : editMode ? (
                <Overlay visible="hover">
                    <Filter />
                </Overlay>
            ) : null}
            <input ref={ref} type="file" onChange={handleChange} style={{display: 'none'}}/>
        </ProfileContainer>
    )
}

export default ProfilePicture
